import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Sun, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { GoogleAdsRequestQuoteLink } from '@/components/GoogleAdsRequestQuoteLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const logoUrl = '/assets/glc-logo.webp';
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let frameId = null;
    const handleScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        const doc = document.documentElement;
        const scrollTop = doc.scrollTop || document.body.scrollTop;
        const scrollHeight = doc.scrollHeight - doc.clientHeight;
        setScrolled(scrollTop > 20);
        setScrollProgress(scrollHeight > 0 ? Math.min(100, (scrollTop / scrollHeight) * 100) : 0);
        frameId = null;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href.split('?')[0]);
  };

  return (
    <nav
      data-testid="navbar"
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled ? 'bg-slate-950/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      )}
    >
      <div
        className="absolute top-0 left-0 h-1 bg-emerald-500 transition-[width] duration-75"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="GLC logo" width="226" height="70" className="h-10 w-auto object-contain" />
            ) : (
              <Sun className="w-10 h-10 text-white" />
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link
              to="/services"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/services') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-services"
            >
              Services
            </Link>
            
            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                    isActive('/products') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid="nav-products-dropdown"
                >
                  Products
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-slate-900 border-slate-800">
                <DropdownMenuItem asChild>
                  <Link to="/products?brand=foxess" className="text-slate-300 hover:text-white cursor-pointer" data-testid="nav-foxess">
                    Fox ESS
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products?brand=sunsynk" className="text-slate-300 hover:text-white cursor-pointer" data-testid="nav-sunsynk">
                    Sunsynk
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products?brand=jasolar" className="text-slate-300 hover:text-white cursor-pointer" data-testid="nav-jasolar">
                    JA Solar
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/inverter-game"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/inverter-game') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-inverter-game"
            >
              Build-A-System
            </Link>
            <Link
              to="/blog"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/blog') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-blog"
            >
              Blog
            </Link>
            <Link
              to="/agrifox"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/agrifox') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-agrifox"
            >
              AgriFox
            </Link>
            <Link
              to="/about"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/about') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-about"
            >
              About
            </Link>
            <Link
              to="/faq"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/faq') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-faq"
            >
              FAQ
            </Link>
            <Link
              to="/contact"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                isActive('/contact') ? 'text-emerald-400' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <GoogleAdsRequestQuoteLink
              href="tel:0686801986"
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
              data-testid="call-button"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">068 680 1986</span>
            </GoogleAdsRequestQuoteLink>
            <Button 
              asChild
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6"
              data-testid="get-quote-nav-btn"
            >
              <GoogleAdsRequestQuoteLink to="/contact">Get a Quote</GoogleAdsRequestQuoteLink>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div 
            className="lg:hidden bg-slate-900 rounded-2xl mt-2 p-4 animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-home"
              >
                Home
              </Link>
              <Link
                to="/services"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/services') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-services"
              >
                Services
              </Link>
              <Link
                to="/products"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/products') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-products"
              >
                Products
              </Link>
              <Link
                to="/inverter-game"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/inverter-game') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-inverter-game"
              >
                Build-A-System
              </Link>
              <Link
                to="/blog"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/blog') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-blog"
              >
                Blog
              </Link>
              <Link
                to="/agrifox"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/agrifox') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-agrifox"
              >
                AgriFox
              </Link>
              <Link
                to="/about"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/about') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-about"
              >
                About
              </Link>
              <Link
                to="/faq"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/faq') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-faq"
              >
                FAQ
              </Link>
              <Link
                to="/contact"
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                  isActive('/contact') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                }`}
                data-testid="mobile-nav-contact"
              >
                Contact
              </Link>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-800">
              <GoogleAdsRequestQuoteLink
                href="tel:0686801986"
                className="flex items-center justify-center gap-2 text-slate-300 py-3"
              >
                <Phone className="w-4 h-4" />
                <span className="font-medium">068 680 1986</span>
              </GoogleAdsRequestQuoteLink>
              <Button 
                asChild
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full mt-2"
              >
                <GoogleAdsRequestQuoteLink to="/contact">Get a Quote</GoogleAdsRequestQuoteLink>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
